import { Check, ThumbsDown, ThumbsUp } from 'react-feather';
import { useEffect, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import AddFeedbackMutation from './AddFeedbackMutation';
import UI from '../UI';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';

export interface FeedbackFormProps {
  label?: string | null;
  projectId?: string | null;
  eventId?: string | null;
  meta?: object;
}

export const FeedbackForm = ({
  label,
  projectId,
  eventId,
  meta,
}: FeedbackFormProps) => {
  const { t } = useTranslation('common');
  const project = useProject();

  const [addFeedback, { loading }] = useMutation(
    AddFeedbackMutation,
  );

  const input = {
    project_id: projectId ?? project?.id,
    event_id: eventId,
    url: window.location.href,
    meta: JSON.stringify({
      userAgent: navigator.userAgent,
      ...meta,
    }),
  };

  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');

  const form = useForm({
    values: {
      comment,
    },
  });

  useEffect(() => {
    setRating(null);
    setComment('');
    form.reset();
  }, [input.project_id, input.url, projectId, eventId]);

  const [openModal, closeModal] = useModal(() => (
    <UI.Modal close={closeModal}>
      <UI.Form onSubmit={() => addComment()}>
        <UI.FormGrid>
          <UI.FlexContainer>
            <UI.Badge sc={{ brand: 'success', size: 18, mr: 1.5 }}>
              <UI.Icon>
                <Check />
              </UI.Icon>
            </UI.Badge>
            <UI.H4>
              {t('feedback_form.feedback_sent')}
            </UI.H4>
          </UI.FlexContainer>

          <UI.InputGroup>
            <UI.TextArea
              {...form.textarea('comment')}
              sc={{ minHeight: 64 }}
              placeholder={t('feedback_form.message_label')}
              aria-label={t('feedback_form.message_label')}
              id="Comment"
            />
          </UI.InputGroup>

          <UI.FormButtons
            form={form}
            submitLabel={(!comment || form.values.comment !== comment || loading)
              ? t('feedback_form.share')
              : (
                <>
                  <UI.Icon>
                    <Check />
                  </UI.Icon>
                  {' '}
                  {t('feedback_form.thank_you')}
                </>
              )}
            onClose={closeModal}
            loading={loading}
            enabled={form.values.comment && form.values.comment !== comment}
          />
        </UI.FormGrid>
      </UI.Form>
    </UI.Modal>
  ), [form, comment, loading]);

  const addPositiveFeedback = () => {
    openModal();

    if (rating !== 1) {
      setRating(1);

      addFeedback({
        variables: {
          input: {
            ...input,
            ...form.values,
            rating: 1,
          },
        },
      });
    }
  };

  const addNegativeFeedback = () => {
    openModal();

    if (rating !== 0) {
      setRating(0);

      addFeedback({
        variables: {
          input: {
            ...input,
            ...form.values,
            rating: 0,
          },
        },
      });
    }
  };

  const addComment = () => {
    setComment(form.values.comment);

    addFeedback({
      variables: {
        input: {
          ...input,
          ...form.values,
          rating,
        },
      },
    });
  };

  return (
    <UI.FlexContainer sc={{ justifyContent: 'center', alignItems: 'center', direction: ['column', 'row'] }}>
      <UI.Div sc={{
        textAlign: 'center', color: 'gray.600', small: true, mr: [0, 2], mb: [2, 0],
      }}
      >
        {label || t('feedback_form.how_do_you_rate_this_page')}
      </UI.Div>
      <UI.Div>
        <UI.Button
          onClick={() => addPositiveFeedback()}
          sc={{
            outline: rating !== 1,
            brand: rating === 1 ? 'success' : 'gray.250',
            color: rating === 1 ? 'white' : 'success',
            size: 'sm',
            mr: 1,
          }}
          aria-label={t('common:feedback_form.thumbs_up')}
        >
          <UI.Icon>
            <ThumbsUp />
          </UI.Icon>
        </UI.Button>
        <UI.Button
          onClick={() => addNegativeFeedback()}
          sc={{
            outline: rating !== 0,
            brand: rating === 0 ? 'error' : 'gray.250',
            color: rating === 0 ? 'white' : 'error',
            size: 'sm',
          }}
          aria-label={t('common:feedback_form.thumbs_down')}
        >
          <UI.Icon>
            <ThumbsDown />
          </UI.Icon>
        </UI.Button>
      </UI.Div>
    </UI.FlexContainer>
  );
};
